import type { CityConfig } from "@/pages/city-template/types";

export const bendConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Deschutes County Clerk Vital Records", desc: "Obtain vital records through the Deschutes County Clerk's office in Bend.", url: "https://www.deschutes.org/clerk" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  city: "Bend",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "bend",
  route: "/cities/bend",
  title: "Emergency Passport Bend, OR — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Bend, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Central Oregon travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Bend, Oregon",
  heroSubtitle: "Planning a ski trip to Japan or a summer hike through the Canadian Rockies? Don't let an expired passport ground you. Fast Passport Center helps Bend and Central Oregon travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Bend%20Oregon%20downtown%20skyline%20with%20Cascade%20Mountains%20in%20background%2C%20Deschutes%20River%20reflecting%20warm%20golden%20hour%20light%2C%20pine%20trees%20and%20high%20desert%20landscape%20surrounding%20the%20city%2C%20dramatic%20snow-capped%20peaks%20visible%2C%20photorealistic%20landscape%20photography%2C%20vibrant%20Pacific%20Northwest%20high%20desert%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=bend-hero-01&orientation=landscape",
  heroImageAlt: "Bend Oregon downtown with Cascade Mountains and Deschutes River at golden hour",
  geoPosition: "44.0582;-121.3153",
  geoPlacename: "Bend, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "Approximately 360 miles, 5.5 hours via US-97",
  },
  nearbyCitiesZips: [
    { name: "Bend", zips: "97701–97709" },
    { name: "Redmond", zips: "97756" },
    { name: "Sisters", zips: "97759" },
    { name: "Prineville", zips: "97754" },
    { name: "Madras", zips: "97741" },
    { name: "La Pine", zips: "97739" },
    { name: "Sunriver", zips: "97707" },
    { name: "Terrebonne", zips: "97760" },
    { name: "Culver", zips: "97734" },
    { name: "Powell Butte", zips: "97753" },
    { name: "Three Rivers", zips: "97707" },
    { name: "Eagle Crest", zips: "97756" },
    { name: "Tumalo", zips: "97701" },
    { name: "Bend River", zips: "97701" },
    { name: "Deschutes River Woods", zips: "97707" },
    { name: "Alfalfa", zips: "97701" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in Bend in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Bend", desc: "FedEx and UPS locations throughout Bend and Redmond offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — especially important for last-minute passport needs before ski season trips." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Tyler K.",
      location: "Bend, OR",
      rating: 5,
      text: "Booked a last-minute heli-skiing trip to British Columbia and realized my passport was expired. Fast Passport Center had my renewal back in 7 days. Shipped everything from the FedEx on Hwy 20 in Bend. Couldn't believe how smooth it was.",
      service: "Passport Renewal",
    },
    {
      name: "Rachel M.",
      location: "Redmond, OR",
      rating: 5,
      text: "Our family needed passports for our kids before a summer trip to Banff. Fast Passport Center made the child passport process so clear — we got both kids' passports in 9 days. The checklist they sent was exactly what we needed.",
      service: "Child Passport",
    },
    {
      name: "James D.",
      location: "Sisters, OR",
      rating: 5,
      text: "Lost my passport two weeks before a business trip to Vancouver. Fast Passport Center guided me through the replacement process and got it to me in 5 days. The daily email updates were clutch — I wasn't stressed for a second.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "bend-main",
      name: "Bend Main Post Office",
      type: "USPS Passport Acceptance",
      address: "751 NW 4th St",
      city: "Bend",
      state: "OR",
      zip: "97701",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm, Sat 9am–1pm",
      distance: "Downtown Bend",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Bend. Appointment required — book online well in advance. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "bend-east",
      name: "Bend Eastside Station",
      type: "USPS Passport Acceptance",
      address: "2100 NE 3rd St",
      city: "Bend",
      state: "OR",
      zip: "97701",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "East Bend — near the Old Mill District",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "East Bend passport acceptance. Confirm appointment and photo service availability before visiting.",
      icon: "ri-mail-line",
    },
    {
      id: "redmond-post",
      name: "Redmond Post Office",
      type: "USPS Passport Acceptance",
      address: "618 SW 5th St",
      city: "Redmond",
      state: "OR",
      zip: "97756",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Redmond — about 17 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Redmond option for northern Deschutes County residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "deschutes-county",
      name: "Deschutes County Clerk",
      type: "County Passport Acceptance",
      address: "1300 NW Wall St, Suite 200",
      city: "Bend",
      state: "OR",
      zip: "97701",
      phone: "541-388-6548",
      hours: "Mon–Fri 8am–5pm (by appointment)",
      distance: "Downtown Bend — County Services Building",
      website: "https://www.deschutes.org/clerk",
      websiteLabel: "Deschutes County Clerk",
      notes: "County clerk passport acceptance in Bend. Appointment required — often faster scheduling than USPS. Call ahead to confirm passport services.",
      icon: "ri-government-line",
    },
    {
      id: "prineville-post",
      name: "Prineville Post Office",
      type: "USPS Passport Acceptance",
      address: "175 NE Compton Ave",
      city: "Prineville",
      state: "OR",
      zip: "97754",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Prineville — about 35 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Crook County option for eastern Deschutes County residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "sisters-post",
      name: "Sisters Post Office",
      type: "USPS Passport Acceptance",
      address: "220 N Pine St",
      city: "Sisters",
      state: "OR",
      zip: "97759",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Sisters — about 22 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Sisters option for western Deschutes County residents. Confirm appointment availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Bend before international travel?",
      a: "Yes. Through Fast Passport Center, Bend travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — don't wait until the last minute before your ski trip or international adventure.",
    },
    {
      q: "How fast can I renew my passport in Bend?",
      a: "Fast passport renewal in Bend is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my trip to Canada or Japan?",
      a: "If your passport expires before your trip to Canada, Japan, or any international destination, start now. Canada requires passports to be valid for the duration of your stay, and Japan requires 6 months validity. Fast Passport Center can expedite your passport renewal so you don't miss your flight out of RDM or PDX.",
    },
    {
      q: "Can I get a same-day passport appointment near Bend?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Bend, Fast Passport Center is the faster and more predictable option — no 5+ hour drive to Seattle required.",
    },
    {
      q: "Can I expedite a child passport in Bend?",
      a: "Yes. Child passport expediting in Bend is possible through Fast Passport Center, but child passports (for minors 15 and under) do require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it lost or stolen and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps and lost passports cannot be used even if found later.",
    },
    {
      q: "Does Bend have a passport agency?",
      a: "Bend, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 325 miles north) and the San Francisco Passport Agency in California (about 480 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Bend residents.",
    },
    {
      q: "How long does a standard passport take in Bend?",
      a: "If you apply through a standard post office acceptance facility in Bend, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers Bend travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I get a passport renewal near me in Bend without visiting an office?",
      a: "Yes. Most Bend-area adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in Bend or Redmond.",
    },
    {
      q: "What airports do Bend passport holders typically fly from?",
      a: "Redmond Municipal Airport (RDM) is the primary airport for Bend and Central Oregon travelers, offering direct flights to Seattle, Portland, Denver, Salt Lake City, and more. Portland International Airport (PDX) is about 160 miles west for additional international options. If your passport isn't in hand, don't book international travel. Fast Passport Center can help you meet your deadline.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Bend West", address: "1465 SW Knoll Ave, Bend, OR 97702", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "CVS — Bend North", address: "3208 N Hwy 97, Bend, OR 97701", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/" },
    { name: "FedEx Office — Bend Downtown", address: "634 NW Wall St, Bend, OR 97701", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM", website: "https://www.fedex.com/" },
    { name: "The UPS Store — Bend East", address: "2220 NE Hwy 20, Bend, OR 97701", hours: "Mon–Fri 8 AM – 7 PM", website: "https://www.theupsstore.com/" },
    { name: "Walmart — Bend South", address: "2015 NE 3rd St, Bend, OR 97701", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/" },
    { name: "Walgreens — Redmond", address: "1515 S Hwy 97, Redmond, OR 97756", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/" },
    { name: "CVS — Redmond", address: "2445 SW 4th St, Redmond, OR 97756", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/" },
    { name: "FedEx Office — Redmond", address: "423 SW 6th St, Redmond, OR 97756", hours: "Mon–Fri 7 AM – 10 PM", website: "https://www.fedex.com/" },
    { name: "Walmart — Redmond", address: "1727 S Hwy 97, Redmond, OR 97756", hours: "Daily 7 AM – 11 PM", website: "https://www.walmart.com/" },
    { name: "The UPS Store — Sisters", address: "311 W Hood Ave, Sisters, OR 97759", hours: "Mon–Fri 8 AM – 6 PM", website: "https://www.theupsstore.com/" },
  ],
  dmvLocations: [
    {
      name: "Bend DMV Office",
      address: "415 SW Airport Way, Bend, OR 97702",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Primary DMV office for Bend and Deschutes County. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Redmond DMV Office",
      address: "938 SW 5th St, Redmond, OR 97756",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Redmond DMV option for northern Deschutes County residents. Appointment recommended.",
      featured: false,
    },
    {
      name: "Madras DMV Office",
      address: "815 SW 5th St, Madras, OR 97741",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Jefferson County option for eastern Deschutes County and Madras area residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "RDM",
      name: "Redmond Municipal Airport (Roberts Field)",
      distance: "Primary airport for Bend — 17 miles north in Redmond",
      address: "2522 Commercial St SE, Redmond, OR 97756",
      note: "RDM serves Central Oregon with direct flights to Seattle, Portland, Denver, Salt Lake City, San Francisco, and Phoenix. The busiest travel seasons are December–March (ski season) and June–August — plan passport needs well in advance.",
      flightUrl: "https://www.flyrdm.com/",
      flightLabel: "RDM Airport Website",
      featured: true,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Alternative international hub — about 160 miles west",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Some Bend travelers use PDX for additional international flight options. Passport required for all international departures.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from RDM in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Bend and Central Oregon.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Bend and Central Oregon travelers renew fast — passport expired before ski season is one of our most common urgent requests.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Bend", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Bend travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Bend families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Bend residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines. Same-day passport help for Bend travelers.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Bend, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Bend residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Bend, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Bend travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Bend families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Bend, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Bend travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your ski trip or summer adventure, especially for travel during December–March or June–August." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before your documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission, critical for last-minute passport renewal in Bend." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Bend runs 6–13 weeks. If your trip to Canada, Japan, or Europe is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — especially for international flights out of RDM. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Bend travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Bend", description: "Drop off at any FedEx or UPS in Bend or Redmond — most locations are open evenings. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (360 miles, 5.5 hours via US-97)", fpc: "Ship documents overnight from Bend" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Total Time Commitment", agency: "Full day driving + waiting at agency", fpc: "10 minutes online + overnight shipping" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/portland" },
    { name: "Fast passport renewal in Eugene", route: "/eugene" },
    { name: "Rush passport service in Redmond", route: "#" },
    { name: "Emergency passport help near Sisters", route: "#" },
    { name: "Fast passport renewal for Central Oregon travelers", route: "/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Bend or Redmond — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Bend and all of Central Oregon remotely",
  serviceAreaIntro: "Whether you are in downtown Bend, Redmond, Sisters, Prineville, Sunriver, or anywhere in the greater Deschutes County area, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Central Oregon zip codes. Anywhere in Deschutes, Crook, or Jefferson County — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Bend as simple as possible. Most Bend clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Bend travelers. Fast Passport Center reviews your documents before submission to catch these issues before they become problems.",
  testimonialsIntro: "Real reviews from Bend, Redmond, and Sisters travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Bend and Central Oregon travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  dmvWebsite: "https://www.oregon.gov/odot/dmv",
  dmvLabel: "Oregon DMV — Official Website",
};